import React, { useMemo } from "react";
import { FormattedMessage } from "react-intl";

import * as t from "@/lib/backend/manufacturing2.generated.types";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { getDay, isoDateFromShiftTag } from "@/lib/util";

import "./PeopleScheduleCalendar.scss";

type Props = {
    shifts: t.IShiftsPeopleEx[];
    year: number;
    month: number;
    onMonthChange: (year: number, month: number) => void;
};

type CalendarCell = {
    date_key: string;
    day: number;
    is_current_month: boolean;
    is_scheduled: boolean;
    is_today: boolean;
};

function toDateKey(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function buildCalendarCells(year: number, month: number, scheduled_dates: Set<string>): CalendarCell[] {
    const today_key = toDateKey(new Date());
    const first_of_month = new Date(year, month, 1);
    const start_weekday = getDay(first_of_month);
    const days_in_month = new Date(year, month + 1, 0).getDate();

    const cells: CalendarCell[] = [];

    for (let i = 0; i < start_weekday; i++) {
        cells.push({
            date_key: `pad-start-${i}`,
            day: 0,
            is_current_month: false,
            is_scheduled: false,
            is_today: false,
        });
    }

    for (let day = 1; day <= days_in_month; day++) {
        const date_key = toDateKey(new Date(year, month, day));
        cells.push({
            date_key,
            day,
            is_current_month: true,
            is_scheduled: scheduled_dates.has(date_key),
            is_today: date_key === today_key,
        });
    }

    while (cells.length % 7 !== 0) {
        cells.push({
            date_key: `pad-end-${cells.length}`,
            day: 0,
            is_current_month: false,
            is_scheduled: false,
            is_today: false,
        });
    }

    return cells;
}

function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
    const date = new Date(year, month + delta, 1);
    return { year: date.getFullYear(), month: date.getMonth() };
}

/**
 * Month calendar highlighting days where the person has shifts, with prev/next/home navigation.
 */
const PeopleScheduleCalendar = ({ shifts, year, month, onMonthChange }: Props) => {
    const now = new Date();
    const is_current_month = year === now.getFullYear() && month === now.getMonth();

    const scheduled_dates = useMemo(() => {
        const dates = new Set<string>();
        for (const shift of shifts) {
            dates.add(isoDateFromShiftTag(shift.shift_tag));
        }
        return dates;
    }, [shifts]);

    const cells = useMemo(() => buildCalendarCells(year, month, scheduled_dates), [year, month, scheduled_dates]);

    const month_title = new Date(year, month, 1).toLocaleString(undefined, {
        month: "long",
        year: "numeric",
    });
    const weekday_labels = [
        translate("common.weekday0", "Monday"),
        translate("common.weekday1", "Tuesday"),
        translate("common.weekday2", "Wednesday"),
        translate("common.weekday3", "Thursday"),
        translate("common.weekday4", "Friday"),
        translate("common.weekday5", "Saturday"),
        translate("common.weekday6", "Sunday"),
    ].map(label => label.slice(0, 2));

    const previous_month_label = translate("Calendar.previous_month", "Previous month");
    const next_month_label = translate("Calendar.next_month", "Next month");
    const current_month_label = translate("Calendar.now", "Now");

    return (
        <div className="people-schedule-calendar">
            <div className="people-schedule-calendar__header">
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-short px-2"
                    onClick={() => {
                        const previous = shiftMonth(year, month, -1);
                        onMonthChange(previous.year, previous.month);
                    }}
                    aria-label={previous_month_label}
                    title={previous_month_label}
                >
                    <i className="fas fa-chevron-left" aria-hidden="true" />
                </button>
                <div className="people-schedule-calendar__title">{month_title}</div>
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-short px-2"
                    onClick={() => {
                        const next = shiftMonth(year, month, 1);
                        onMonthChange(next.year, next.month);
                    }}
                    aria-label={next_month_label}
                    title={next_month_label}
                >
                    <i className="fas fa-chevron-right" aria-hidden="true" />
                </button>
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-short px-2"
                    onClick={() => onMonthChange(now.getFullYear(), now.getMonth())}
                    aria-label={current_month_label}
                    title={current_month_label}
                    disabled={is_current_month}
                >
                    <i className="fas fa-home" aria-hidden="true" />
                </button>
            </div>
            <div className="people-schedule-calendar__weekdays">
                {weekday_labels.map((label, index) => (
                    <div key={index} className="people-schedule-calendar__weekday">
                        {label}
                    </div>
                ))}
            </div>
            <div className="people-schedule-calendar__grid">
                {cells.map(cell => {
                    if (!cell.is_current_month) {
                        return <div key={cell.date_key} className="people-schedule-calendar__day is-empty" />;
                    }
                    const class_names = [
                        "people-schedule-calendar__day",
                        cell.is_today ? "is-today" : "",
                        cell.is_scheduled ? "is-scheduled" : "",
                    ]
                        .filter(Boolean)
                        .join(" ");
                    return (
                        <div
                            key={cell.date_key}
                            className={class_names}
                            title={
                                cell.is_scheduled
                                    ? translate("Manufacturing.People.scheduled_day", "Scheduled")
                                    : undefined
                            }
                        >
                            <span className="people-schedule-calendar__day-number">{cell.day}</span>
                            {cell.is_scheduled && <span className="people-schedule-calendar__dot" />}
                        </div>
                    );
                })}
            </div>
            <div className="people-schedule-calendar__legend">
                <span className="people-schedule-calendar__legend-item">
                    <span className="people-schedule-calendar__dot" />
                    <FormattedMessage id="Manufacturing.People.scheduled_day" defaultMessage="Scheduled" />
                </span>
            </div>
        </div>
    );
};

export default PeopleScheduleCalendar;
